import { ButtonRadioGroup, Select } from '@payaca/ambrosia';
import { FC, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';

import { useUpdateMyPreferences } from '@/api/mutations/user/useUpdateMyPreferences';
import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyAccountFeatures from '@/api/queries/me/useGetMyAccountFeatures';
import { useTranslation } from '@/i18n';

import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';
import DashboardInvoices from './components/DashboardInvoices';
import DashboardProposals from './components/DashboardProposals';
import DashboardQuickStats from './components/DashboardQuickStats';
import DashboardStatusBreakdown from './components/DashboardStatusBreakdown';
import DashboardSummary from './components/DashboardSummary';
import { DateRangePreset, getDateRangeFromPreset } from './utils';

const DashboardNewPage: FC = () => {
  const translate = useTranslation('dashboard');
  const translatePages = useTranslation('pages');
  const history = useHistory();
  const { preferences, user, isLoading } = useGetMe();
  const { data: features, isLoading: isLoadingFeatures } =
    useGetMyAccountFeatures();
  const { mutate: updatePreferences } = useUpdateMyPreferences();
  const useNewDashboard = preferences?.useNewDashboard ?? false;
  // Accounts without this feature (newly-created accounts) cannot return to the
  // classic dashboard, so the toggle is hidden and the fallback is skipped (P-7524).
  const hasLegacyDashboard = features?.includes('hasLegacyDashboard') ?? false;
  const [dateRangePreset, setDateRangePreset] =
    useState<DateRangePreset>('last-3-months');

  const handleDashboardChange = (value: 'classic' | 'new') => {
    updatePreferences(
      { useNewDashboard: value === 'new' },
      {
        onError: (error) => {
          console.error('Failed to update dashboard preference', error);
        },
      }
    );
  };

  useEffect(() => {
    if (isLoading || isLoadingFeatures) return;
    if (hasLegacyDashboard && !useNewDashboard) {
      history.replace('/dashboard');
    }
  }, [useNewDashboard, hasLegacyDashboard, isLoading, isLoadingFeatures, history]);

  const dateRangeOptions: { label: string; value: DateRangePreset }[] = useMemo(
    () => [
      { label: translate('dateRange.thisMonth'), value: 'this-month' },
      { label: translate('dateRange.lastMonth'), value: 'last-month' },
      { label: translate('dateRange.last3Months'), value: 'last-3-months' },
      { label: translate('dateRange.last6Months'), value: 'last-6-months' },
      { label: translate('dateRange.last12Months'), value: 'last-12-months' },
      { label: translate('dateRange.allTime'), value: 'all-time' },
    ],
    [translate]
  );

  const dateRange = useMemo(
    () => getDateRangeFromPreset(dateRangePreset),
    [dateRangePreset]
  );

  const comparisonPeriodLabel = useMemo(() => {
    const presetToComparison: Record<DateRangePreset, string | undefined> = {
      'this-month': translate('dateRange.previousThisMonth'),
      'last-month': translate('dateRange.previousLastMonth'),
      'last-3-months': translate('dateRange.previous3Months'),
      'last-6-months': translate('dateRange.previous6Months'),
      'last-12-months': translate('dateRange.previous12Months'),
      'all-time': undefined,
    };
    return presetToComparison[dateRangePreset];
  }, [dateRangePreset, translate]);

  return (
    <>
      <Helmet title={translatePages('dashboard.title')} />
      <AuthenticatedPageWrapper className="dashboard-new-page">
        <div className="typography:prose mx-auto max-w-screen-2xl space-y-6 px-4 py-6 @screen-sm:px-6 @screen-lg:px-8">
          {/* Header with greeting, date range, and dashboard toggle */}
          <div className="space-y-2">
            <div className="flex flex-col gap-2 @screen-sm:flex-row @screen-sm:items-center @screen-sm:justify-between @screen-sm:gap-4">
              <h1>
                {translate('greeting', {
                  firstName: user?.fullName?.split(' ')[0] ?? '',
                  companyName: user?.account?.name ?? '',
                })}
              </h1>
              {hasLegacyDashboard && (
                <ButtonRadioGroup
                  value={useNewDashboard ? 'new' : 'classic'}
                  onChange={handleDashboardChange}
                  sizeVariant="sm"
                  options={[
                    { label: 'Classic', value: 'classic' as const },
                    { label: 'New', value: 'new' as const },
                  ]}
                />
              )}
            </div>
            <div className="flex flex-col gap-1 @screen-sm:w-fit @screen-sm:flex-row @screen-sm:items-center @screen-sm:gap-2">
              <p className="supporting-body text-gray-500">
                {translate('dateRange.label')}
              </p>
              <Select
                options={dateRangeOptions}
                value={dateRangePreset}
                onChange={(value) => value && setDateRangePreset(value)}
                multiple={false}
              />
            </div>
          </div>

          {/* Quick Stats Row */}
          <section>
            <DashboardQuickStats dateRange={dateRange} />
          </section>

          {/* Summary with period comparison */}
          <section>
            <DashboardSummary
              dateRange={dateRange}
              comparisonPeriodLabel={comparisonPeriodLabel}
            />
          </section>

          {/* Status Breakdown Donuts */}
          <section>
            <DashboardStatusBreakdown dateRange={dateRange} />
          </section>

          {/* Trends Header */}
          <div className="pt-4">
            <h2>{translate('trends')}</h2>
          </div>

          {/* Proposals */}
          <section>
            <DashboardProposals dateRange={dateRange} />
          </section>

          {/* Invoices */}
          <section>
            <DashboardInvoices dateRange={dateRange} />
          </section>
        </div>
      </AuthenticatedPageWrapper>
    </>
  );
};

export default DashboardNewPage;
